// calibrationLogBookForm/calibrationLogBookFormActions.js (aggregator)
// Mirrors makeSamplingEventFormActions but only manages the calibration log section + book fields.

import { makeCalibrationLogBookSectionActions } from "./calibrationLogBookSectionActions";

export const makeCalibrationLogBookFormActions = (set, get) => {
  const api = {
    getCurrentParent: () =>
      get().activeCalibrationLogBook?.currentBook || null,

    setCurrentParent: (next) =>
      set((s) => ({
        activeCalibrationLogBook: {
          ...s.activeCalibrationLogBook,
          currentBook: next,
        },
      })),

    getFormUi: () =>
      get().activeCalibrationLogBook?.calibrationLogBookForm || { rev: 0 },

    setFormUi: (nextFormUi) =>
      set((s) => ({
        activeCalibrationLogBook: {
          ...s.activeCalibrationLogBook,
          calibrationLogBookForm: nextFormUi,
        },
      })),

    bump: (patch = null) => {
      const prev = api.getFormUi();
      const nextRev = (prev.rev || 0) + 1;
      api.setFormUi(
        patch ? { ...prev, rev: nextRev, ...patch } : { ...prev, rev: nextRev }
      );
    },

    bumpRevision: () =>
      set((s) => ({
        activeCalibrationLogBook: {
          ...s.activeCalibrationLogBook,
          _rev: (s.activeCalibrationLogBook._rev || 0) + 1,
        },
      })),

    setCalibrationLogUi: (patchOrFn) => {
      const prevForm = api.getFormUi();
      const prev = prevForm?.calibrationLogUI || {};
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      api.setFormUi({ ...prevForm, calibrationLogUI: { ...prev, ...patch } });
    },
  };

  // Helper: snake_case key -> camelCase
  const toCamelCase = (str) =>
    str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

  // Apply patch fields to an object using setter methods or direct assignment
  const applyPatchToObject = (target, patch, skipKeys = ["id", "action"]) => {
    Object.entries(patch).forEach(([key, value]) => {
      if (skipKeys.includes(key)) return;
      const camelKey = toCamelCase(key);
      const setterName = `set${camelKey.charAt(0).toUpperCase()}${camelKey.slice(1)}`;
      if (typeof target[setterName] === "function") {
        target[setterName](value);
      } else if (camelKey in target) {
        target[camelKey] = value;
      }
    });
  };

  const calibrationLogs = makeCalibrationLogBookSectionActions(api);

  // Book-level field updates (name, date, project/phase/task). Each mutates the active book model,
  // bumps the revision for re-render, and emits a `calibration_log_book` patch (parent id injected
  // by the autosave hook).
  const emitBookPatch = async (fields, emitPatch) => {
    const book = api.getCurrentParent();
    if (!book) return { ok: false };
    api.bumpRevision();
    if (typeof emitPatch === "function") {
      return emitPatch({
        patch_type: "calibration_log_book",
        action: "update",
        id: book.id,
        ...fields,
      });
    }
    return { ok: true };
  };

  const bookInfo = {
    setBookName: async (value, emitPatch) => {
      const book = api.getCurrentParent();
      if (!book) return;
      book.setBookName(value);
      return emitBookPatch({ book_name: value }, emitPatch);
    },
    // value is a YYYY-MM-DD string for the API; the model stores whatever display value is passed.
    setBookDate: async (displayValue, apiValue, emitPatch) => {
      const book = api.getCurrentParent();
      if (!book) return;
      book.setBookDate(displayValue);
      return emitBookPatch({ book_date: apiValue }, emitPatch);
    },
    selectProject: async (projectNumber, emitPatch) => {
      const book = api.getCurrentParent();
      if (!book) return;
      book.setProjectNumber(projectNumber);
      book.setPhaseId(null);
      book.setTaskId(null);
      return emitBookPatch(
        { project_number: projectNumber, phase_id: null, task_id: null },
        emitPatch
      );
    },
    selectPhase: async (phaseId, emitPatch) => {
      const book = api.getCurrentParent();
      if (!book) return;
      book.setPhaseId(phaseId);
      book.setTaskId(null);
      return emitBookPatch({ phase_id: phaseId, task_id: null }, emitPatch);
    },
    selectTask: async (taskId, emitPatch) => {
      const book = api.getCurrentParent();
      if (!book) return;
      book.setTaskId(taskId);
      return emitBookPatch({ task_id: taskId }, emitPatch);
    },
  };

  // Book-level field updates (book_name, status, completion flags)
  const handleBookBroadcast = (patch) => {
    const book = api.getCurrentParent();
    if (!book) return;
    applyPatchToObject(book, patch);
    api.bump();
  };

  const handleBroadcast = (patchData) => {
    const { patch_type, patch } = patchData;
    switch (patch_type) {
      case "calibration_log_book_entry":
        if (calibrationLogs.handleBroadcast) {
          calibrationLogs.handleBroadcast(patch, applyPatchToObject);
        }
        break;
      case "calibration_log_book":
        handleBookBroadcast(patch);
        break;
      default:
        console.warn(
          "Unknown calibration log book broadcast patch_type:",
          patch_type
        );
    }
    api.bumpRevision();
  };

  return Object.freeze({
    calibrationLogs,
    bookInfo,
    handleBroadcast,
  });
};